/**
 * voice-v8 (Pre.974) — Dokument → Vorlese-Abschnitte.
 *
 * Zerlegt beliebigen Markdown/Text in sprechfertige Abschnitte für den
 * geteilten Vorlese-Player (buildNarrationPlayer.setSections):
 *   - Gibt es ##- (bzw. ersatzweise #-) Überschriften → kapitelweise,
 *     lange Kapitel werden zusätzlich längen-basiert unterteilt.
 *   - Sonst (z.B. ein PDF→Text-Export ganz OHNE Überschriften, wie das Buch
 *     „Der Einklang des Paradoxen") → rein längen-basiert an Absatzgrenzen.
 * Jeder Abschnitt wird markdown-bereinigt, weil der Streaming-TTS-Pfad
 * (/api/tts/prepare → renderTextToMp3) den Text NICHT reinigt — sonst läse
 * der Bot „Doppelkreuz", „Sternchen" usw. mit.
 */

interface DocSection {
  title: string;
  text: string;
}

/** Ziel-Zeichen pro Abschnitt (~2-3 Min Audio) — angenehm zum Blättern/Resumen. */
const TARGET_CHARS = 3200;
/** Harte Obergrenze pro Abschnitt (= interne TTS-Chunk-Größe in tts-render). */
const MAX_CHARS = 4500;

/**
 * Markdown → saubere Sprech-Prosa (kompakte Variante von markdownToNarration).
 *
 * ⚠️ 08.09.2026 exportiert: Die Chronik baut ihre Abschnitte aus EINZELNEN
 *    Eintraegen (Monate, Tage) statt aus einem Dokument — sie braucht die
 *    Saeuberung, aber nicht das Zerlegen. Ohne sie liest die Stimme
 *    „Doppelkreuz" und „Sternchen" mit.
 */
export function mdToSpeech(md: string): string {
  return (md ?? "")
    .replace(/```[\s\S]*?```/g, " ") // Code-Blöcke
    .replace(/`([^`]+)`/g, "$1") // Inline-Code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ") // Bilder
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // Links → nur Text
    .replace(/^\s{0,3}#{1,6}\s+/gm, "") // Überschriften-Marker
    .replace(/^\s{0,3}>\s?/gm, "") // Blockzitate
    /*
     * ⚠️ 08.09.2026 — die Trennlinie muss GANZ FRUEH weg, vor Listen und
     *    vor fett/kursiv. Stand sie hinter ihnen, hatte die Listen-Regel
     *    aus „- - -" schon „- -" gemacht und die Kursiv-Regel aus „***"
     *    ein einzelnes „*". Uebrig blieben Zeichen, die die Stimme mitliest
     *    — gefunden beim Vorlesen der Chronik.
     */
    .replace(/^\s*([-*_])\s*(\1\s*){2,}$/gm, "") // horizontale Linien
    .replace(/^\s*[-*+]\s+/gm, "") // Listen-Marker
    .replace(/^\s*\d+\.\s+/gm, "") // nummerierte Listen
    .replace(/(\*\*|__)([\s\S]*?)\1/g, "$2") // fett
    .replace(/(\*|_)([\s\S]*?)\1/g, "$2") // kursiv
    .replace(/\|/g, " ") // Tabellen-Pipes
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Packt (bereits bereinigte) Absätze zu Abschnitten ~TARGET_CHARS. */
function packParagraphs(cleanText: string): DocSection[] {
  const paras = cleanText
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const out: DocSection[] = [];
  let buf = "";
  let no = 1;
  const flush = (): void => {
    const t = buf.trim();
    if (t) {
      out.push({ title: `Abschnitt ${no}`, text: t });
      no++;
    }
    buf = "";
  };
  for (const p of paras) {
    if (buf && buf.length + p.length + 2 > TARGET_CHARS) flush();
    buf = buf ? `${buf}\n\n${p}` : p;
    if (buf.length >= MAX_CHARS) flush();
  }
  flush();
  return out;
}

/** Zerlegt ein Dokument in Vorlese-Abschnitte (Kapitel oder Längen-Pakete). */
export function splitDocChapters(md: string): DocSection[] {
  const raw = (md ?? "").trim();
  if (!raw) return [];

  const lines = raw.split("\n");
  const hasH2 = lines.some((l) => /^\s{0,3}##\s+/.test(l));
  const hasH1 = lines.some((l) => /^\s{0,3}#\s+/.test(l));
  const boundary = hasH2
    ? /^\s{0,3}##\s+(.*)$/
    : hasH1
      ? /^\s{0,3}#\s+(.*)$/
      : null;

  if (!boundary) {
    // Keine Überschriften-Struktur → rein längen-basiert.
    return packParagraphs(mdToSpeech(raw));
  }

  // Kapitelweise; lange Kapitel zusätzlich packen.
  const chapters: Array<{ title: string; body: string[] }> = [];
  let cur: { title: string; body: string[] } = { title: "Anfang", body: [] };
  for (const l of lines) {
    const m = boundary.exec(l);
    if (m) {
      chapters.push(cur);
      cur = { title: (m[1] ?? "").trim() || "Kapitel", body: [] };
    } else {
      cur.body.push(l);
    }
  }
  chapters.push(cur);

  const out: DocSection[] = [];
  for (const ch of chapters) {
    const clean = mdToSpeech(ch.body.join("\n"));
    if (!clean) continue;
    if (clean.length <= MAX_CHARS) {
      out.push({ title: ch.title, text: clean });
    } else {
      const parts = packParagraphs(clean);
      parts.forEach((p, i) =>
        out.push({
          title: parts.length > 1 ? `${ch.title} (${i + 1})` : ch.title,
          text: p.text,
        }),
      );
    }
  }
  return out.length ? out : packParagraphs(mdToSpeech(raw));
}

/**
 * Welchen Text der Datei-Betrachter vorliest. (23.09.2026, Ticket 2285)
 *
 * Liegt eine fertige Hörfassung vor, liest der Spieler SIE, sonst den
 * Rohtext (frontend/lib/project-file-viewer.ts). Der Server wärmt beim
 * Öffnen genau diesen Text vor (server/projects.ts) — der Zwischenspeicher
 * hängt am Abschnittstext, also müssen beide Seiten dieselbe Wahl treffen.
 *
 * ⚠️ Bis heute stand die Regel NUR im Browser, und der Server wärmte den
 *    Rohtext: „Kapitel-01-Haltung.md" zerfiel dort in 25 Abschnitte, der
 *    Spieler spielte 11 (Wachhund-Titel „Datei 7/11", 16.09.). Die
 *    Vorwärmung vom 15.09. (39,8 min) traf keinen einzigen davon.
 *
 * Leer heißt: keine Hörfassung — dann gilt der Rohtext.
 */
export function hoerfassungText(
  narration: { status?: string | null; body?: string | null } | null | undefined,
): string {
  return narration?.status === "done" ? (narration.body ?? "").trim() : "";
}
